import { create } from 'zustand';
import type { EstimationResult } from '../types/estimation';
import { fetchRatios, fetchBenchmarks } from '../api/reference';
import type { RatioItem, BenchmarkItem } from '../api/reference';

export interface ProgrammeItem {
  id: string;
  category: string;
  label: string;
  dimension: number;
  dimensionType: string;
  unitCost: number;
  totalCost: number;
}

export interface CustomSubsidy {
  id: string;
  label: string;
  organe: string;
  category: string;
  mode: 'pct' | 'fixed';
  taux: number;     // percentage (0-100) if mode=pct
  montant: number;  // fixed amount if mode=fixed
  active: boolean;
}

interface EstimationState {
  ha: number;
  haZi: number;
  niveau: 1 | 2;
  activeTab: string;
  n2Pct: Record<string, number>;
  n2Commune: string;
  n2EcoleCT: boolean;
  result: EstimationResult | null;
  r2: EstimationResult | null;
  tenantRatios: Record<string, number>;
  tenantBenchmarks: Record<string, number>;
  ratiosLoaded: boolean;
  ratiosError: string | null;

  // Full ratio/benchmark objects for the RatiosTab
  ratioItems: RatioItem[];
  benchmarkItems: BenchmarkItem[];

  // Per-project ratio overrides
  projectRatioOverrides: Record<string, number>;

  // Custom subsidies
  customSubsidies: CustomSubsidy[];

  // Subsidy toggles
  paChEnabled: boolean;
  paGeEnabled: boolean;

  // Programme détaillé
  programmeDetail: ProgrammeItem[];

  // Project fields
  horizonYears: number;
  zoneOverrides: Record<string, Record<string, number>>;

  setHa: (ha: number) => void;
  setHaZi: (haZi: number) => void;
  setNiveau: (niveau: 1 | 2) => void;
  setActiveTab: (tab: string) => void;
  setN2: (key: string, value: number) => void;
  resetN2: () => void;
  setN2Commune: (commune: string) => void;
  setN2EcoleCT: (v: boolean) => void;
  setResult: (result: EstimationResult) => void;
  setR2: (r2: EstimationResult) => void;
  loadTenantRatios: () => Promise<void>;
  reloadTenantRatios: () => Promise<void>;
  hydrate: (data: Partial<Pick<EstimationState, 'ha' | 'haZi' | 'niveau' | 'n2Pct' | 'n2Commune' | 'n2EcoleCT' | 'result' | 'r2' | 'paChEnabled' | 'paGeEnabled' | 'customSubsidies' | 'programmeDetail'>>) => void;

  // Per-project overrides
  setProjectRatioOverrides: (overrides: Record<string, number>) => void;

  // Custom subsidy actions
  addCustomSubsidy: () => void;
  updateCustomSubsidy: (id: string, updates: Partial<Omit<CustomSubsidy, 'id'>>) => void;
  removeCustomSubsidy: (id: string) => void;

  // Programme détaillé actions
  addProgrammeItem: (category: string) => void;
  updateProgrammeItem: (id: string, updates: Partial<Omit<ProgrammeItem, 'id'>>) => void;
  removeProgrammeItem: (id: string) => void;
  clearProgrammeCategory: (category: string) => void;

  // Subsidy toggle setters
  setPaChEnabled: (v: boolean) => void;
  setPaGeEnabled: (v: boolean) => void;

  // Project setters
  setHorizonYears: (years: number) => void;
  setZoneOverride: (zone: string, key: string, value: number) => void;
  resetZoneOverrides: () => void;
}

const N2_KEYS = [
  'logements_per_ha',
  'inhabitants_per_logement',
  'sbp_per_logement',
  'pct_activites_sbp',
  'pct_espaces_verts_surface',
  'classes_per_logements_short',
  'classes_per_logements_long',
  'creche_places_per_logements',
  'infra_per_logement',
  'ecole_per_class',
  'creche_per_place',
  'co_cost',
  'sport_cost',
  'park_per_m2',
];

const defaultN2Pct = Object.fromEntries(N2_KEYS.map((k) => [k, 0]));

async function fetchAndApplyRatios(set: (partial: Partial<EstimationState>) => void) {
  const [ratios, benchmarks] = await Promise.all([fetchRatios(), fetchBenchmarks()]);
  const ratioMap: Record<string, number> = {};
  for (const r of ratios) ratioMap[r.key] = r.value;
  const benchmarkMap: Record<string, number> = {};
  for (const b of benchmarks) benchmarkMap[b.key] = b.value;
  set({
    tenantRatios: ratioMap,
    tenantBenchmarks: benchmarkMap,
    ratioItems: ratios,
    benchmarkItems: benchmarks,
    ratiosLoaded: true,
  });
}

export const useEstimationStore = create<EstimationState>((set, get) => ({
  ha: 30,
  haZi: 0,
  niveau: 1,
  activeTab: 'estimation',
  n2Pct: { ...defaultN2Pct },
  n2Commune: '',
  n2EcoleCT: false,
  result: null,
  r2: null,
  tenantRatios: {},
  tenantBenchmarks: {},
  ratiosLoaded: false,
  ratiosError: null,
  ratioItems: [],
  benchmarkItems: [],
  projectRatioOverrides: {},

  // Programme détaillé default
  programmeDetail: [],

  // Custom subsidies default
  customSubsidies: [],

  // Subsidy toggle defaults
  paChEnabled: true,
  paGeEnabled: true,

  // Project defaults
  horizonYears: 30,
  zoneOverrides: {},

  setHa: (ha) => set({ ha }),
  setHaZi: (haZi) => set({ haZi }),
  setNiveau: (niveau) => set({ niveau }),
  setActiveTab: (activeTab) => set({ activeTab }),
  setN2: (key, value) =>
    set((state) => ({ n2Pct: { ...state.n2Pct, [key]: value } })),
  resetN2: () => set({ n2Pct: { ...defaultN2Pct } }),
  setN2Commune: (n2Commune) => set({ n2Commune }),
  setN2EcoleCT: (n2EcoleCT) => set({ n2EcoleCT }),
  setResult: (result) => set({ result }),
  setR2: (r2) => set({ r2 }),
  hydrate: (data) => set(data),

  // Per-project overrides
  setProjectRatioOverrides: (overrides) => set({ projectRatioOverrides: overrides }),

  // Custom subsidy actions
  addCustomSubsidy: () =>
    set((state) => ({
      customSubsidies: [
        ...state.customSubsidies,
        {
          id: crypto.randomUUID(),
          label: 'Nouvelle subvention',
          organe: '',
          category: 'general',
          mode: 'fixed' as const,
          taux: 0,
          montant: 0,
          active: true,
        },
      ],
    })),
  updateCustomSubsidy: (id, updates) =>
    set((state) => ({
      customSubsidies: state.customSubsidies.map((cs) =>
        cs.id === id ? { ...cs, ...updates } : cs
      ),
    })),
  removeCustomSubsidy: (id) =>
    set((state) => ({
      customSubsidies: state.customSubsidies.filter((cs) => cs.id !== id),
    })),

  // Programme détaillé actions
  addProgrammeItem: (category) =>
    set((state) => ({
      programmeDetail: [
        ...state.programmeDetail,
        {
          id: crypto.randomUUID(),
          category,
          label: '',
          dimension: 0,
          dimensionType: 'm²',
          unitCost: 0,
          totalCost: 0,
        },
      ],
    })),
  updateProgrammeItem: (id, updates) =>
    set((state) => ({
      programmeDetail: state.programmeDetail.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ),
    })),
  removeProgrammeItem: (id) =>
    set((state) => ({
      programmeDetail: state.programmeDetail.filter((item) => item.id !== id),
    })),
  clearProgrammeCategory: (category) =>
    set((state) => ({
      programmeDetail: state.programmeDetail.filter((item) => item.category !== category),
    })),

  // Subsidy toggle setters
  setPaChEnabled: (paChEnabled) => set({ paChEnabled }),
  setPaGeEnabled: (paGeEnabled) => set({ paGeEnabled }),

  // Project setters
  setHorizonYears: (years) => set({ horizonYears: years }),
  setZoneOverride: (zone, key, value) =>
    set((state) => ({
      zoneOverrides: {
        ...state.zoneOverrides,
        [zone]: { ...(state.zoneOverrides[zone] || {}), [key]: value },
      },
    })),
  resetZoneOverrides: () => set({ zoneOverrides: {} }),

  loadTenantRatios: async () => {
    if (get().ratiosLoaded) return;
    try {
      set({ ratiosError: null });
      await fetchAndApplyRatios(set);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load ratios';
      set({ ratiosError: message });
    }
  },

  reloadTenantRatios: async () => {
    try {
      set({ ratiosError: null });
      await fetchAndApplyRatios(set);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reload ratios';
      set({ ratiosError: message });
    }
  },
}));
